import { IntegrationSource } from '@modules/integrationsV2/types';
import {
  getIntegrationChannelsMetadata,
  getIsIntegrationIngestionInProgress,
} from '@modules/integrationsV2/utils';
import { getIsIncompleteIntegrationData } from '@modules/integrationsV2/utils/getIsIncompleteIntegrationData';
import {
  IntegrationDirection,
  IntegrationType,
  IntegrationViewFragment,
} from 'generated/graphql';

export const getIntegrationSourcesGroupedByType = (
  integrations: IntegrationViewFragment[],
  isIntegrationContextEnabled: boolean, // TODO: remove this after integration context is enabled by default
) => {
  const groupedSources: Record<string, IntegrationSource> = {};

  const integrationStatusGroupedByUniqueKey =
    getIntegrationStatusGroupedByUniqueSourceKey(integrations);

  integrations.forEach((integration) => {
    if (integration.direction !== IntegrationDirection.Inbound) {
      return;
    }

    const {
      channel,
      namespace,
      id,
      onboardingProgress,
      integrationType,
      source,
      eqlFieldsGroupID,
      description: integrationDescription,
      externalLinkKey,
    } = integration;

    const uniqueSourceKey = getUniqueIntegrationSourceKey(integration);

    const { isDisabled, isIngestionInProgress, isIncompleteIntegration } =
      integrationStatusGroupedByUniqueKey[uniqueSourceKey];

    const isSingleConnectionSource =
      Boolean(namespace) || integrationType === IntegrationType.Cohorts;

    const channelsMetadata = getIntegrationChannelsMetadata(
      channel.type,
      integration.data,
    );

    if (
      isIntegrationContextEnabled &&
      channelsMetadata &&
      (!channelsMetadata.length || !integration.isActive)
    ) {
      return;
    }

    if (!groupedSources[uniqueSourceKey]) {
      const integrationSource: IntegrationSource = {
        integrationType,
        channel,
        namespace,
        source,
        eqlFieldsGroupID,
        isDisabled,
        isIngestionInProgress,
        isIncompleteIntegration,
        isSingleConnectionSource,
        integrationID: isSingleConnectionSource ? id : undefined,
        onboardingProgress: isSingleConnectionSource
          ? onboardingProgress
          : undefined,
        description: isSingleConnectionSource
          ? integrationDescription?.description
          : undefined,
        externalLinkKey,
      };
      groupedSources[uniqueSourceKey] = integrationSource;
    }
  });

  const sourcesGroupedByIntegrationType: Record<
    IntegrationType,
    IntegrationSource[]
  > = {
    [IntegrationType.Feedback]: [],
    [IntegrationType.User]: [],
    [IntegrationType.Account]: [],
    [IntegrationType.Cohorts]: [],
    [IntegrationType.Comms]: [],
  };

  Object.values(groupedSources).forEach((integrationSource) => {
    sourcesGroupedByIntegrationType[integrationSource.integrationType].push(
      integrationSource,
    );
  });

  Object.values(sourcesGroupedByIntegrationType).forEach((sources) => {
    sources.sort((a, b) => {
      if (a.isDisabled && !b.isDisabled) {
        return 1; // disabled integration sources at the bottom
      }

      if (b.isDisabled && !a.isDisabled) {
        return -1; // enabled integration sources at the top
      }

      const nameA = `${a.channel.displayName} ${a.namespace || ''}`;
      const nameB = `${b.channel.displayName} ${b.namespace || ''}`;
      return nameA.localeCompare(nameB);
    });
  });

  return sourcesGroupedByIntegrationType;
};

const getUniqueIntegrationSourceKey = (
  integration: IntegrationViewFragment,
) => {
  return `${integration.channel.type}_${integration.integrationType}_${
    integration.namespace ?? ''
  }`;
};

const getIntegrationStatusGroupedByUniqueSourceKey = (
  integrations: IntegrationViewFragment[],
) => {
  const groupedObj: Record<
    string,
    {
      isDisabled: boolean;
      isIngestionInProgress: boolean;
      isIncompleteIntegration: boolean;
    }
  > = {};

  integrations.forEach((integration) => {
    const uniqueSourceKey = getUniqueIntegrationSourceKey(integration);

    if (groupedObj[uniqueSourceKey] === undefined) {
      groupedObj[uniqueSourceKey] = {
        isDisabled: true, // default value is true, because we want to show disabled only if all integrations are disabled for this source
        isIngestionInProgress: false,
        isIncompleteIntegration: false,
      };
    }

    const isDisabled = integration.isActive === false;

    if (!isDisabled) {
      groupedObj[uniqueSourceKey].isDisabled = false;
    }

    const isIncompleteIntegration = getIsIncompleteIntegrationData({
      integrationData: integration.data,
      channelType: integration.channel.type,
      isDisabledIntegration: isDisabled,
    });

    if (isIncompleteIntegration) {
      groupedObj[uniqueSourceKey].isIncompleteIntegration = true;
    }

    const isIngestionInProgress = getIsIntegrationIngestionInProgress({
      onboardingProgress: integration.onboardingProgress,
      isDisabled: isDisabled,
      isIncompleteIntegration,
    });

    if (isIngestionInProgress) {
      groupedObj[uniqueSourceKey].isIngestionInProgress = true;
    }
  });

  return groupedObj;
};
